/**
 * UnifiedGoogleTokenManager - Centralized Google OAuth token management
 * 
 * Handles all Google API authentication in one place:
 * - Google Drive (for Handbooks)
 * - Google Contacts (for Personal Contacts)
 * - Google Directory (for Organization Directory)
 * 
 * Features:
 * - Single authentication flow for all scopes
 * - Secure encrypted token storage
 * - Automatic token refresh
 * - Proactive token management
 * - Cross-component token sharing
 */

import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../firebase';
import cryptoUtils from '../utils/cryptoUtils';

class UnifiedGoogleTokenManager {
  constructor() {
    this.TOKEN_KEY = 'unified_google_tokens';
    this.ENCRYPTION_KEY = 'unified_encryption_key';
    this.TOKEN_EXPIRY = 50 * 60 * 1000; // 50 minutes (buffer before Google's 60min)
    this.REFRESH_THRESHOLD = 15 * 60 * 1000; // Refresh when 15 minutes left (more aggressive to prevent expiry)
    // Use localStorage instead of sessionStorage for persistence across browser sessions
    this.storage = typeof Storage !== 'undefined' ? localStorage : sessionStorage;
    
    // All Google scopes we need across the app
    this.REQUIRED_SCOPES = [
      'https://www.googleapis.com/auth/drive.readonly',
      'https://www.googleapis.com/auth/contacts.readonly',
      'https://www.googleapis.com/auth/directory.readonly'
    ];
    
    // Scope type mappings for easier access
    this.SCOPE_TYPES = {
      drive: 'https://www.googleapis.com/auth/drive.readonly',
      contacts: 'https://www.googleapis.com/auth/contacts.readonly',
      directory: 'https://www.googleapis.com/auth/directory.readonly',
      combined: 'all' // Special type for all scopes
    };
    
    // Retry configuration
    this.retryConfig = {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 30000,
      backoffMultiplier: 2
    };
    
    // Token refresh management
    this.refreshTimeouts = new Map();
    this.refreshPromises = new Map();
    this.eventListeners = new Set();
    this.backgroundMonitoringInterval = null;
    this.backgroundMonitoringEnabled = false;
    
    // Performance metrics
    this.metrics = {
      refreshAttempts: 0,
      refreshSuccesses: 0,
      refreshFailures: 0,
       scopeValidations: 0,
       tokenRetrievals: 0
    };
    
     // Initialize config object
    this.config = {
      encryptionKeyRotationInterval: 24 * 60 * 60 * 1000 // 24 hours
    };
    
    // Initialize asynchronously to avoid blocking constructor
    setTimeout(() => {
      this.initialize().catch(error => {
        console.warn('UnifiedGoogleTokenManager initialization failed:', error);
        // Set a flag so methods can try to reinitialize
        this.initializationFailed = true;
      });
    }, 0);
  }

  /**
   * Migrate tokens from sessionStorage to localStorage for persistence
   * This ensures existing users don't lose their tokens when we switch storage
   */
  migrateTokensFromSessionStorage() {
    try {
      const sessionKeys = Object.keys(sessionStorage);
      let migratedCount = 0;
      
      sessionKeys.forEach(key => {
        if (key.startsWith(this.TOKEN_KEY)) {
          const tokenData = sessionStorage.getItem(key);
          if (tokenData) {
            // Check if it doesn't already exist in localStorage
            if (!this.storage.getItem(key)) {
              this.storage.setItem(key, tokenData);
              migratedCount++;
            }
            // Remove from sessionStorage after migration
            sessionStorage.removeItem(key);
          }
        }
      });
      
      if (migratedCount > 0) {
        console.log(`✅ Migrated ${migratedCount} token(s) from sessionStorage to localStorage for persistence`);
      }
    } catch (error) {
      console.warn('Token migration failed:', error);
    }
  }

  /**
   * Initialize encryption for secure token storage
   */
  async initializeEncryption() {
    try {
      if (!this.encryptionKey) {
        // Try to get existing key or generate new one
        const storedKey = localStorage.getItem(this.ENCRYPTION_KEY);
        if (storedKey) {
          try {
            const keyData = JSON.parse(storedKey);
            this.encryptionKey = await cryptoUtils.importKey(keyData);
          } catch (importError) {
            console.warn('Failed to import stored key, generating new one:', importError);
            // Generate a new key if import fails
            this.encryptionKey = await cryptoUtils.generateEncryptionKey();
            const exportedKey = await cryptoUtils.exportKey(this.encryptionKey);
            localStorage.setItem(this.ENCRYPTION_KEY, JSON.stringify(exportedKey));
          }
        } else {
          // Generate new extractable key for storage
          this.encryptionKey = await cryptoUtils.generateEncryptionKey();
          const exportedKey = await cryptoUtils.exportKey(this.encryptionKey);
          localStorage.setItem(this.ENCRYPTION_KEY, JSON.stringify(exportedKey));
        }
      }
    } catch (error) {
      console.error('Encryption initialization failed:', error);
      // Fall back to unencrypted storage (not recommended for production)
      this.encryptionKey = null;
    }
  }

  /**
   * Validate configuration and environment
   */
     validateConfiguration() {
    const validation = {
      isValid: true,
      errors: [],
      warnings: []
    };

     // Ensure config object exists
     if (!this.config) {
       this.config = {};
     }

    // Check and set client ID with fallback
    this.config.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 
                          window.REACT_APP_GOOGLE_CLIENT_ID ||
                          '423543714005-v6o10ma13gj1dbjp3ke6sn1d6q5j3v5o.apps.googleusercontent.com';
    
    if (!this.config.clientId || this.config.clientId === 'undefined') {
      validation.warnings.push('Google Client ID not found in environment, using fallback');
      // Don't mark as invalid, just warn
    }

    // Check Web Crypto API support
    if (!window.crypto || !window.crypto.subtle) {
      validation.warnings.push('Web Crypto API not available - using fallback storage');
    }

    // Check Firebase Auth
    if (!auth) {
      validation.errors.push('Firebase Auth not initialized');
      validation.isValid = false;
    }

    return validation;
  }

  /**
   * Initialize the token manager
   */
  async initialize() {
    try {
      await this.initializeEncryption();
      
      const validation = this.validateConfiguration();
      if (!validation.isValid) {
        throw new Error(`Configuration validation failed: ${validation.errors.join(', ')}`);
      }
      
      if (validation.warnings.length > 0) {
        console.warn('Configuration warnings:', validation.warnings);
      }

      // Migrate any existing tokens from sessionStorage to localStorage
      this.migrateTokensFromSessionStorage();
      
      // Setup automatic cleanup of expired tokens
      this.scheduleTokenCleanup();
      
      // Setup global background token monitoring (works across all pages)
      this.setupBackgroundTokenMonitoring();
      
      console.log('✅ UnifiedGoogleTokenManager initialized successfully');
      return true;
    } catch (error) {
      console.error('❌ UnifiedGoogleTokenManager initialization failed:', error);
      throw error;
    }
  }

  /**
   * Get a valid token for the specified scope type
   */
  async getToken(userId, scopeType = 'combined') {
    try {
      if (!this.isInitialized) {
        if (this.initializationFailed) {
          // Try to reinitialize
          this.initializationFailed = false;
        }
        await this.initialize();
      }

       this.metrics.tokenRetrievals++;

      const tokenInfo = await this.getStoredToken(userId);
      
      if (!tokenInfo) {
        throw new Error('No token available - authentication required');
      }

      // Validate scope access
      if (scopeType !== 'combined') {
        const requiredScope = this.SCOPE_TYPES[scopeType];
        let hasScope = false;
        let tokenScopes = [];
        
        // First check if scope is stored in token info (faster, no API call)
        if (tokenInfo.scope) {
          tokenScopes = typeof tokenInfo.scope === 'string' 
            ? tokenInfo.scope.split(' ') 
            : Array.isArray(tokenInfo.scope) 
              ? tokenInfo.scope 
              : [];
          
          hasScope = tokenScopes.includes(requiredScope);
          
          if (hasScope) {
            // Scope is in stored token info, good to go
            console.log(`✅ Scope validation: Token has ${scopeType} scope`);
          } else {
            console.log(`⚠️ Scope ${scopeType} not found in stored scopes: ${tokenScopes.join(', ')}`);
            // Scope not in stored info, try API validation as fallback
            console.log(`🔄 Attempting API validation for ${scopeType} scope...`);
            hasScope = await this.validateTokenScope(tokenInfo, scopeType);
            
            if (hasScope) {
              // Scope exists but wasn't in stored info - update stored scope
              console.log(`✅ API validation: Token has ${scopeType} scope, updating stored scope info`);
              if (tokenInfo.scope && !tokenScopes.includes(requiredScope)) {
                tokenScopes.push(requiredScope);
                tokenInfo.scope = tokenScopes.join(' ');
                // Optionally save updated scope info (don't await to avoid blocking)
                this.storeToken(userId, {
                  accessToken: tokenInfo.accessToken,
                  refreshToken: tokenInfo.refreshToken,
                  clientId: tokenInfo.clientId,
                  scope: tokenInfo.scope,
                  token_type: tokenInfo.tokenType || 'Bearer'
                }, Math.floor((tokenInfo.expiresAt - Date.now()) / 1000)).catch(err => {
                  console.warn('Failed to update scope info:', err);
                });
              }
            }
          }
        } else {
          // No scope info stored, validate via API
          console.log(`🔄 No stored scope info, validating via API for ${scopeType}...`);
          hasScope = await this.validateTokenScope(tokenInfo, scopeType);
          
          if (hasScope) {
            // Store scope info for next time
            const apiScopes = await this.getTokenScopesFromAPI(tokenInfo.accessToken);
            if (apiScopes.length > 0) {
              tokenInfo.scope = apiScopes.join(' ');
              // Save updated scope info
              this.storeToken(userId, {
                accessToken: tokenInfo.accessToken,
                refreshToken: tokenInfo.refreshToken,
                clientId: tokenInfo.clientId,
                scope: tokenInfo.scope,
                token_type: tokenInfo.tokenType || 'Bearer'
              }, Math.floor((tokenInfo.expiresAt - Date.now()) / 1000)).catch(err => {
                console.warn('Failed to save scope info:', err);
              });
            }
          }
        }
        
        if (!hasScope) {
          const scopeList = tokenScopes.length > 0 ? tokenScopes.join(', ') : 'unknown';
          throw new Error(`Token does not have required scope: ${scopeType} (drive.readonly). Available scopes: ${scopeList}. Please re-authenticate to grant all required permissions.`);
        }
      }

      // Check if token needs refresh
      if (this.needsRefresh(tokenInfo)) {
        try {
          const refreshedToken = await this.refreshToken(userId, tokenInfo);
          return refreshedToken?.accessToken;
        } catch (refreshError) {
          // If refresh fails due to missing refresh token, indicate auth is required
          if (refreshError.code === 'AUTH_REQUIRED' || 
              refreshError.message.includes('No refresh token available') ||
              refreshError.message.includes('authentication required')) {
            // Remove the expired/invalid token
            this.removeToken(userId);
            throw new Error('No token available - authentication required');
          }
          // Re-throw other refresh errors
          throw refreshError;
        }
      }

      return tokenInfo.accessToken;
    } catch (error) {
      console.error('Token retrieval failed:', error);
      
      // Ensure authentication-required errors are clear
      if (error.message.includes('authentication required') || 
          error.message.includes('No token available') ||
          error.message.includes('No refresh token available')) {
        throw new Error('No token available - authentication required');
      }
      
      throw error;
    }
  }

  /**
   * Store token securely with encryption
   */
  async storeToken(userId, tokenData, expiresIn = 3600) {
    try {
      const tokenInfo = {
        accessToken: tokenData.accessToken,
        refreshToken: tokenData.refreshToken,
        clientId: tokenData.clientId || this.config?.clientId,
        scope: tokenData.scope,
        tokenType: tokenData.token_type || 'Bearer',
        expiresAt: Date.now() + (expiresIn * 1000),
        createdAt: Date.now(),
        userId: userId
      };

      const tokenJson = JSON.stringify(tokenInfo);
      
      if (this.encryptionKey) {
        try {
          const { encrypted, iv } = await cryptoUtils.encrypt(tokenJson, this.encryptionKey);
          // Convert ArrayBuffers to base64 strings for storage
          const encryptedBase64 = cryptoUtils.arrayBufferToBase64(encrypted);
          const ivBase64 = cryptoUtils.arrayBufferToBase64(iv);
          // Store as JSON string
          const encryptedData = JSON.stringify({ data: encryptedBase64, iv: ivBase64 });
          this.storage.setItem(`${this.TOKEN_KEY}_${userId}`, encryptedData);
        } catch (encryptError) {
          console.error('Encryption failed, storing unencrypted:', encryptError);
          // Fallback to unencrypted storage if encryption fails
          this.storage.setItem(`${this.TOKEN_KEY}_${userId}`, tokenJson);
        }
      } else {
        // Fallback to unencrypted storage
        this.storage.setItem(`${this.TOKEN_KEY}_${userId}`, tokenJson);
      }

      // Schedule proactive refresh
      this.scheduleTokenRefresh(userId, tokenInfo.expiresAt);

      // Trigger immediate background health check to ensure token is monitored
      // This ensures tokens are refreshed even when user is on other pages
      if (this.backgroundMonitoringEnabled) {
        // Don't await - let it run in background
        this.performBackgroundTokenHealthCheck().catch(err => {
          console.warn('Background health check after token storage failed:', err);
        });
      }

      console.log('✅ Token stored successfully for user:', userId);
      return true;
    } catch (error) {
      console.error('Token storage failed:', error);
      return false;
    }
  }

  /**
   * Get stored token and decrypt
   */
  async getStoredToken(userId) {
    try {
      // Ensure encryption is initialized
      if (!this.encryptionKey && this.isInitialized) {
        await this.initializeEncryption();
      }

      const storedData = this.storage.getItem(`${this.TOKEN_KEY}_${userId}`);

      if (!storedData) {
        return null;
      }

      let tokenJson;
      
      // Try to parse as encrypted data first
      try {
        const parsed = JSON.parse(storedData);
        
        // Check if it's encrypted data (has 'data' and 'iv' properties)
        if (parsed.data && parsed.iv && this.encryptionKey) {
          try {
            // Convert base64 strings back to ArrayBuffers
            const encryptedBuffer = cryptoUtils.base64ToArrayBuffer(parsed.data);
            const ivBuffer = new Uint8Array(cryptoUtils.base64ToArrayBuffer(parsed.iv));
            
            // Decrypt using the encryption key
            tokenJson = await cryptoUtils.decrypt(encryptedBuffer, ivBuffer, this.encryptionKey);
          } catch (decryptError) {
            console.warn('Decryption failed, trying unencrypted format:', decryptError);
            // If decryption fails, try as plain JSON
            tokenJson = storedData;
          }
        } else {
          // Plain JSON token (unencrypted)
          tokenJson = storedData;
        }
      } catch (parseError) {
        // If parsing fails, assume it's plain JSON (legacy unencrypted format)
        tokenJson = storedData;
      }

      const tokenInfo = JSON.parse(tokenJson);

      // Validate token hasn't expired
      if (Date.now() >= tokenInfo.expiresAt) {
        // If token expired but we have a refresh token, try to refresh it automatically
        if (tokenInfo.refreshToken) {
          console.log('🔄 Token expired, attempting automatic refresh...');
          try {
            await this.refreshToken(userId, tokenInfo);
            // Return the refreshed token info
            return await this.getStoredToken(userId);
          } catch (refreshError) {
            console.warn('Automatic token refresh failed:', refreshError);
            // If refresh fails due to missing refresh token, remove token so auth is triggered
            if (refreshError.code === 'AUTH_REQUIRED' || 
                refreshError.message.includes('No refresh token available')) {
              console.log('⚠️ No refresh token available, removing expired token to trigger re-authentication');
            }
            // Remove expired/invalid token
            this.removeToken(userId);
            return null;
          }
        } else {
          // No refresh token available, remove expired token so re-authentication is triggered
          console.log('⚠️ Token expired and no refresh token available, removing token to trigger re-authentication');
          this.removeToken(userId);
          return null;
        }
      }

      return tokenInfo;
    } catch (error) {
      console.error('Token retrieval failed:', error);
      return null;
    }
  }

  /**
   * Remove token for user
   */
  removeToken(userId) {
    this.storage.removeItem(`${this.TOKEN_KEY}_${userId}`);
    
    // Clear any scheduled refresh
    if (this.refreshTimeouts.has(userId)) {
      clearTimeout(this.refreshTimeouts.get(userId));
      this.refreshTimeouts.delete(userId);
    }
  }

  /**
   * Clear all tokens (for logout)
   */
  clearAllTokens() {
    const keys = Object.keys(this.storage);
    keys.forEach(key => {
      if (key.startsWith(this.TOKEN_KEY)) {
        this.storage.removeItem(key);
      }
    });
    
    // Clear all refresh timeouts
    this.refreshTimeouts.forEach(timeout => clearTimeout(timeout));
    this.refreshTimeouts.clear();
    this.refreshPromises.clear();
  }

  /**
   * Check if token needs refresh
   */
  needsRefresh(tokenInfo) {
    const timeLeft = tokenInfo.expiresAt - Date.now();
    return timeLeft <= this.REFRESH_THRESHOLD;
  }

  /**
   * Get token scopes from API
   */
  async getTokenScopesFromAPI(accessToken) {
    try {
      const response = await fetch(`https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${accessToken}`);
      
      if (!response.ok) {
        return [];
      }

      const tokenDetails = await response.json();
      return tokenDetails.scope ? tokenDetails.scope.split(' ') : [];
    } catch (error) {
      console.error('Failed to get token scopes from API:', error);
      return [];
    }
  }

  /**
   * Validate that token has required scope
   */
  async validateTokenScope(tokenInfo, scopeType) {
    try {
      this.metrics.scopeValidations++;
      
      if (scopeType === 'combined') {
        return true; // Combined type accepts any valid token
      }

      const requiredScope = this.SCOPE_TYPES[scopeType];
      if (!requiredScope) {
        throw new Error(`Unknown scope type: ${scopeType}`);
      }

      // Check token info endpoint to verify actual scopes
      const actualScopes = await this.getTokenScopesFromAPI(tokenInfo.accessToken);
      
      return actualScopes.includes(requiredScope);
    } catch (error) {
      console.error('Scope validation failed:', error);
      return false;
    }
  }

  /**
   * Refresh token using refresh token
   */
  async refreshToken(userId, currentToken = null, retryCount = 0) {
    try {
      // Prevent multiple simultaneous refreshes for same user
      if (this.refreshPromises.has(userId)) {
        return await this.refreshPromises.get(userId);
      }

      const refreshPromise = this.executeTokenRefresh(userId, currentToken, retryCount);
      this.refreshPromises.set(userId, refreshPromise);

      try {
        const result = await refreshPromise;
        this.refreshPromises.delete(userId);
        return result;
      } catch (error) {
        this.refreshPromises.delete(userId);
        throw error;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.emitAuthenticationRequired(userId, error);
      throw error;
    }
  }

  /**
   * Execute the actual token refresh
   */
  async executeTokenRefresh(userId, currentToken = null, retryCount = 0) {
    try {
      this.metrics.refreshAttempts++;
      
      // Get current token if not provided
      if (!currentToken) {
        currentToken = await this.getStoredToken(userId);
      }

      if (!currentToken || !currentToken.refreshToken) {
        // No refresh token means we need to re-authenticate - don't retry
        const error = new Error('No refresh token available - authentication required');
        error.code = 'AUTH_REQUIRED';
        throw error;
      }

      console.log(`🔄 Attempting unified token refresh (attempt ${retryCount + 1})`);

      // Use Google's OAuth2 endpoint to refresh the token
      const refreshResponse = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: currentToken.clientId || process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
          refresh_token: currentToken.refreshToken,
          grant_type: 'refresh_token'
        })
      });

      if (!refreshResponse.ok) {
        const errorText = await refreshResponse.text();
        throw new Error(`OAuth refresh failed: ${refreshResponse.status} ${errorText}`);
      }

      const refreshedTokens = await refreshResponse.json();
      console.log('✅ OAuth token refresh successful');

      // Create updated token data
      const updatedTokenData = {
        accessToken: refreshedTokens.access_token,
        refreshToken: refreshedTokens.refresh_token || currentToken.refreshToken,
        clientId: currentToken.clientId,
        scope: currentToken.scope,
        token_type: refreshedTokens.token_type || 'Bearer'
      };

      // Store the new token
      await this.storeToken(userId, updatedTokenData, refreshedTokens.expires_in || 3600);

      this.metrics.refreshSuccesses++;
      console.log('✅ Unified token refresh completed successfully');
      
      // Emit success event
      this.emitTokenRefreshed(userId);
      
      return {
        accessToken: refreshedTokens.access_token,
        expiresAt: Date.now() + ((refreshedTokens.expires_in || 3600) * 1000)
      };

    } catch (error) {
      console.error(`❌ Token refresh attempt ${retryCount + 1} failed:`, error);

      // Don't retry if there's no refresh token - user needs to re-authenticate
      if (error.code === 'AUTH_REQUIRED' || error.message.includes('No refresh token available')) {
        throw error; // Propagate immediately
      }

      // Implement exponential backoff retry for other errors
      if (retryCount < this.retryConfig.maxRetries) {
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffMultiplier, retryCount),
          this.retryConfig.maxDelay
        );

        console.log(`⏳ Retrying token refresh in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        
        return this.executeTokenRefresh(userId, currentToken, retryCount + 1);
      }

      this.metrics.refreshFailures++;
      throw error;
    }
  }

  /**
   * Perform authentication with all required scopes
   * Uses Google OAuth2 directly to get refresh tokens
   */
  async authenticate(userId, forceReauth = false) {
    try {
      console.log('🔐 Starting unified Google authentication...');

      // Check if this is the first authentication or if we have an existing token
      const existingToken = await this.getStoredToken(userId);
      const isFirstAuth = !existingToken;
      
      // Only use 'consent' prompt on first authentication or when explicitly forcing reauth
      // For subsequent authentications, Google remembers the permissions, so 'select_account' is sufficient
      // This prevents unnecessary permission prompts
      const useConsentPrompt = forceReauth || isFirstAuth;
      
      const provider = new GoogleAuthProvider();
      
      // Add all required scopes
      this.REQUIRED_SCOPES.forEach(scope => {
        provider.addScope(scope);
      });
    
      // Configure provider for reliable scope access
      // Use 'consent' only on first auth or force reauth to avoid unnecessary permission prompts
      // Note: Firebase doesn't support 'access_type' parameter in setCustomParameters
      // Google will remember permissions from first consent, so subsequent auths can use select_account
      provider.setCustomParameters({
        prompt: useConsentPrompt ? 'consent' : 'select_account',
        include_granted_scopes: 'true' // Include previously granted scopes
      });

      console.log('📋 Auth parameters:', {
        prompt: useConsentPrompt ? 'consent' : 'select_account',
        include_granted_scopes: 'true',
        isFirstAuth,
        forceReauth,
        hasExistingToken: !!existingToken
      });

      // Perform authentication
      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
    
      if (!credential.accessToken) {
        throw new Error('No access token received from Google OAuth');
      }

      // Try to get refresh token from the result object directly
      // Firebase doesn't expose refreshToken in credential, but we can check the internal token response
      let refreshToken = credential.refreshToken || null;
      
      // Check if Firebase stores it in the result object's internal _tokenResponse
      // This is a Firebase-internal property that may contain the refresh token
      if (!refreshToken && result && result._tokenResponse) {
        refreshToken = result._tokenResponse.oauthRefreshToken || null;
        if (refreshToken) {
          console.log('✅ Found refresh token in Firebase token response');
        }
      }
      
      // Check user's stsTokenManager which Firebase uses internally
      if (!refreshToken && result && result.user && result.user.stsTokenManager) {
        refreshToken = result.user.stsTokenManager.refreshToken || null;
        if (refreshToken) {
          console.log('✅ Found refresh token in Firebase user stsTokenManager');
        }
      }
      
      // If still no refresh token and we used consent prompt, this is a Firebase limitation
      if (!refreshToken) {
        console.warn('⚠️ No refresh token available from Firebase authentication.');
        console.warn('   Firebase Authentication does not expose refresh tokens to client applications.');
        console.warn('   This means tokens cannot be automatically refreshed when they expire (~1 hour).');
        console.warn('   Users will need to re-authenticate periodically.');
        console.warn('   To enable automatic token refresh, consider implementing Google OAuth2 directly.');
      }

      // Verify scopes were granted
      const tokenInfo = await this.verifyTokenScopes(credential.accessToken);
      
      // Check if all required scopes were granted
      const missingScopes = this.REQUIRED_SCOPES.filter(scope => 
        !tokenInfo.actualScopes.includes(scope)
      );
      
      if (missingScopes.length > 0 && !forceReauth) {
        console.warn('⚠️ Some required scopes were not granted:', missingScopes);
        console.log('💡 Tip: Use forceReauth=true to ensure all scopes are requested');
      }
      
      // If we still don't have a refresh token, try one more approach:
      // Exchange the access token for a refresh token (this doesn't work, but let's document it)
      if (!refreshToken) {
        // Unfortunately, Google OAuth2 doesn't allow exchanging access tokens for refresh tokens
        // The refresh token must be obtained during the initial authorization flow
        console.warn('⚠️ CRITICAL: No refresh token available. Token cannot be refreshed automatically.');
        console.warn('   The access token will expire in ~1 hour and user will need to re-authenticate.');
        console.warn('   To fix this, we need to implement Google OAuth2 directly instead of Firebase.');
      } else {
        console.log('✅ Refresh token received - token can be automatically refreshed');
      }
      
      const tokenData = {
        accessToken: credential.accessToken,
        refreshToken: refreshToken,
        clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: tokenInfo.scope, // Store full scope string
        token_type: 'Bearer'
      };

      await this.storeToken(userId, tokenData, 3600);
      
      console.log('✅ Unified Google authentication completed successfully');
      console.log('🔍 Granted scopes:', tokenInfo.scope);
      console.log('📋 Actual scopes array:', tokenInfo.actualScopes);
      console.log('❌ Missing scopes:', missingScopes.length > 0 ? missingScopes : 'none');
      
      if (missingScopes.length > 0) {
        console.warn('⚠️ WARNING: Not all required scopes were granted during authentication!');
        console.warn('   Missing:', missingScopes.join(', '));
        console.warn('   This may cause issues when accessing features that require these scopes.');
        console.warn('   Consider using forceReauth=true to request all scopes.');
      }
      
      return {
        success: true,
        scopes: tokenInfo.actualScopes,
        accessToken: credential.accessToken
      };
      
    } catch (error) {
      console.error('❌ Unified authentication failed:', error);
      
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Authentication cancelled by user');
      }
      
      throw error;
    }
  }

  /**
   * Verify what scopes were actually granted
   */
  async verifyTokenScopes(accessToken) {
    try {
      const response = await fetch(`https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${accessToken}`);
      
      if (!response.ok) {
        throw new Error('Failed to verify token scopes');
      }

      const tokenInfo = await response.json();
      const actualScopes = tokenInfo.scope ? tokenInfo.scope.split(' ') : [];
      
      // Check which required scopes are missing
      const missingScopes = this.REQUIRED_SCOPES.filter(scope => !actualScopes.includes(scope));
      
      if (missingScopes.length > 0) {
        console.warn('⚠️ Missing required scopes:', missingScopes);
      }

      return {
        scope: tokenInfo.scope,
        actualScopes,
        missingScopes,
        expiresIn: tokenInfo.expires_in
      };
    } catch (error) {
      console.error('Scope verification failed:', error);
      return {
        scope: '',
        actualScopes: [],
        missingScopes: this.REQUIRED_SCOPES,
        expiresIn: 3600
      };
    }
  }

  /**
   * Get token health status
   */
  async getTokenHealth(userId, scopeType = 'combined') {
    try {
      const tokenInfo = await this.getStoredToken(userId);
      
      if (!tokenInfo) {
        return {
          status: 'missing',
          needsRefresh: true,
          needsAuth: true,
          scopes: []
        };
      }

      const timeLeft = tokenInfo.expiresAt - Date.now();
      const needsRefresh = timeLeft <= this.REFRESH_THRESHOLD;
      
      // Validate scope if specific type requested
      let hasRequiredScope = true;
      if (scopeType !== 'combined') {
        hasRequiredScope = await this.validateTokenScope(tokenInfo, scopeType);
      }
      
      return {
        status: hasRequiredScope ? 'valid' : 'insufficient_scope',
        expiresIn: timeLeft,
        needsRefresh,
        needsAuth: !hasRequiredScope,
        createdAt: tokenInfo.createdAt,
        scopes: tokenInfo.scope ? tokenInfo.scope.split(' ') : [],
        hasRefreshToken: !!tokenInfo.refreshToken
      };
    } catch (error) {
      console.error('Token health check failed:', error);
      return {
        status: 'error', 
        needsRefresh: true,
        needsAuth: true,
        error: error.message,
        scopes: []
      };
    }
  }

  /**
   * Schedule proactive token refresh
   */
  scheduleTokenRefresh(userId, expiresAt) {
    // Clear existing timeout
    if (this.refreshTimeouts.has(userId)) {
      clearTimeout(this.refreshTimeouts.get(userId));
    }

    // Calculate when to refresh (10 minutes before expiry)
    const refreshTime = expiresAt - this.REFRESH_THRESHOLD - Date.now();
    
    if (refreshTime > 0) {
      const timeout = setTimeout(async () => {
        try {
          console.log('⏰ Proactive token refresh triggered for user:', userId);
          await this.refreshToken(userId);
    } catch (error) {
          console.error('Proactive token refresh failed:', error);
        }
      }, refreshTime);
      
      this.refreshTimeouts.set(userId, timeout);
    }
  }

  /**
   * Setup global background token monitoring
   * This works regardless of which page the user is on
   */
  setupBackgroundTokenMonitoring() {
    if (this.backgroundMonitoringEnabled) {
      return; // Already set up
    }

    this.backgroundMonitoringEnabled = true;

    // Check token health and refresh proactively every 3 minutes
    // This ensures tokens are refreshed even when user is on other pages
    this.backgroundMonitoringInterval = setInterval(() => {
      this.performBackgroundTokenHealthCheck();
    }, 3 * 60 * 1000); // Every 3 minutes

    // Also check when page becomes visible (user switches back to tab)
    if (typeof document !== 'undefined') {
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          // Page became visible, check tokens immediately
          console.log('📄 Page became visible, checking token health...');
          this.performBackgroundTokenHealthCheck();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Also check on window focus (user switches back to window)
      const handleFocus = () => {
        console.log('👁️ Window focused, checking token health...');
        this.performBackgroundTokenHealthCheck();
      };

      window.addEventListener('focus', handleFocus);

      // Store cleanup handlers
      this.visibilityCleanup = () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
      };
    }

    console.log('✅ Background token monitoring enabled (works globally across all pages)');
  }

  /**
   * Perform background health check on all stored tokens
   * This runs periodically regardless of which page the user is on
   */
  async performBackgroundTokenHealthCheck() {
    try {
      // Safety check: ensure storage is available
      if (!this.storage || typeof this.storage.getItem !== 'function') {
        console.warn('⚠️ Background: Storage not available for token health check');
        return;
      }

      const keys = Object.keys(this.storage);
      const tokenKeys = keys.filter(key => key.startsWith(this.TOKEN_KEY));

      if (tokenKeys.length === 0) {
        return; // No tokens to check
      }

      for (const key of tokenKeys) {
        try {
          const userId = key.replace(`${this.TOKEN_KEY}_`, '');
          
          // Get token health status
          const health = await this.getTokenHealth(userId, 'combined');

          // If token needs refresh, refresh it automatically
          if (health.needsRefresh && health.hasRefreshToken) {
            console.log(`🔄 Background: Auto-refreshing token for user ${userId} (expires in ${Math.round(health.expiresIn / 60000)} minutes)`);
            try {
              await this.refreshToken(userId);
              console.log(`✅ Background: Token refreshed successfully for user ${userId}`);
            } catch (refreshError) {
              console.warn(`⚠️ Background: Token refresh failed for user ${userId}:`, refreshError);
              // Don't throw - continue checking other tokens
            }
          }
          // If token is expired but has refresh token, refresh it
          else if (health.expiresIn <= 0 && health.hasRefreshToken) {
            console.log(`🔄 Background: Token expired, auto-refreshing for user ${userId}`);
            try {
              await this.refreshToken(userId);
              console.log(`✅ Background: Expired token refreshed successfully for user ${userId}`);
            } catch (refreshError) {
              console.warn(`⚠️ Background: Failed to refresh expired token for user ${userId}:`, refreshError);
            }
          }
        } catch (error) {
          console.warn(`⚠️ Background: Error checking token for key ${key}:`, error);
          // Continue with other tokens
        }
      }
    } catch (error) {
      console.error('❌ Background token health check failed:', error);
    }
  }

  /**
   * Stop background monitoring (for cleanup/testing)
   */
  stopBackgroundMonitoring() {
    if (this.backgroundMonitoringInterval) {
      clearInterval(this.backgroundMonitoringInterval);
      this.backgroundMonitoringInterval = null;
    }

    if (this.visibilityCleanup) {
      this.visibilityCleanup();
      this.visibilityCleanup = null;
    }

    this.backgroundMonitoringEnabled = false;
    console.log('🛑 Background token monitoring stopped');
  }

  /**
   * Schedule periodic token cleanup
   */
  scheduleTokenCleanup() {
    setInterval(() => {
      this.cleanupExpiredTokens();
    }, 60 * 60 * 1000); // Run every hour
  }

  /**
   * Clean up expired tokens
   */
  async cleanupExpiredTokens() {
      const keys = Object.keys(this.storage);
    let cleanedCount = 0;
    
    for (const key of keys) {
      if (key.startsWith(this.TOKEN_KEY)) {
        try {
          const data = this.storage.getItem(key);
          if (!data) continue;
          
          // Try to decrypt if encrypted
          let tokenInfo;
          try {
            const parsed = JSON.parse(data);
            
            // Check if it's encrypted data (has 'data' and 'iv' properties)
            if (parsed.data && parsed.iv && this.encryptionKey) {
              try {
                // Convert base64 strings back to ArrayBuffers
                const encryptedBuffer = cryptoUtils.base64ToArrayBuffer(parsed.data);
                const ivBuffer = new Uint8Array(cryptoUtils.base64ToArrayBuffer(parsed.iv));
                
                // Decrypt using the encryption key
                const decrypted = await cryptoUtils.decrypt(encryptedBuffer, ivBuffer, this.encryptionKey);
                tokenInfo = JSON.parse(decrypted);
              } catch (decryptError) {
                // If decryption fails, skip this token
                console.warn('Failed to decrypt token during cleanup:', decryptError);
                continue;
              }
            } else {
              // Plain JSON token (unencrypted)
              tokenInfo = parsed;
            }
          } catch (parseError) {
            // If parsing fails, assume it's plain JSON (legacy unencrypted format)
            try {
              tokenInfo = JSON.parse(data);
            } catch (finalError) {
              // Invalid token data, remove it
              this.storage.removeItem(key);
              cleanedCount++;
              continue;
            }
          }
          
          // Only remove if token is expired AND has no refresh token (can't be renewed)
          if (Date.now() >= tokenInfo.expiresAt && !tokenInfo.refreshToken) {
            this.storage.removeItem(key);
            cleanedCount++;
          }
          // If token expired but has refresh token, try to refresh it automatically
          else if (Date.now() >= tokenInfo.expiresAt && tokenInfo.refreshToken) {
            // Extract userId from key
            const userId = key.replace(`${this.TOKEN_KEY}_`, '');
            // Trigger refresh in background (don't await)
            // Note: We don't increment cleanedCount here as it's async - the item removal happens but counting is unreliable
            this.refreshToken(userId, tokenInfo).catch(err => {
              console.warn('Auto-refresh during cleanup failed:', err);
              // Only remove if refresh failed
              this.storage.removeItem(key);
            });
          }
        } catch (error) {
          // Invalid token data, remove it
          this.storage.removeItem(key);
          cleanedCount++;
          }
        }
    }
    
    if (cleanedCount > 0) {
      console.log(`🧹 Cleaned up ${cleanedCount} expired tokens`);
    }
  }

  /**
   * Event handling for auth state changes
   */
  onAuthenticationRequired(callback) {
    this.eventListeners.add(callback);
    return () => this.eventListeners.delete(callback);
  }

  emitAuthenticationRequired(userId, error) {
    const event = {
      type: 'auth_required',
      userId,
      error: error.message,
        timestamp: new Date().toISOString()
    };

    this.eventListeners.forEach(callback => {
      try {
        callback(event);
    } catch (error) {
        console.error('Auth event callback failed:', error);
      }
    });
    }

  emitTokenRefreshed(userId) {
    const event = {
      type: 'token_refreshed',
      userId,
      timestamp: new Date().toISOString()
    };
    
    this.eventListeners.forEach(callback => {
        try {
        callback(event);
        } catch (error) {
        console.error('Token refresh event callback failed:', error);
  }
    });
  }

  /**
   * Get performance metrics
   */
  getMetrics() {
    return {
      ...this.metrics,
      refreshSuccessRate: this.metrics.refreshAttempts > 0 ? 
        (this.metrics.refreshSuccesses / this.metrics.refreshAttempts) * 100 : 0
    };
  }

  /**
   * Check if token manager is ready
   */
  get isInitialized() {
    return this.encryptionKey !== undefined;
  }
}

// Export singleton instance
export const unifiedGoogleTokenManager = new UnifiedGoogleTokenManager();
export default unifiedGoogleTokenManager;